"use client";

import type { LeadFormDeliveryContext } from "@homeinstead/forms/types";
import type { FormStep } from "./form-types";

import { LEAD_FORM_DELIVERY_CONTEXT } from "@homeinstead/forms/lead-form";
import { stepsHavePostalCodeInput } from "./form-step-utils";
import { useLeadFormPersistedValues } from "./LeadFormPersistedValuesContext";

type PersistedFieldInputsProps = {
  deliveryContext?: LeadFormDeliveryContext | null;
  steps: FormStep[];
};

/**
 * Renders hidden inputs for form submission: persisted field values and,
 * when the form has a postal code step or uses office-local delivery, merged
 * postalCode/location. Reads persisted values from context rather than props
 * — this component always renders inside LeadForm.tsx's provider.
 */
function PersistedFieldInputs({
  deliveryContext,
  steps,
}: PersistedFieldInputsProps) {
  const { persistedFieldValues, postalLocationValues } =
    useLeadFormPersistedValues();
  const hasRuntimePostalLocation =
    deliveryContext === LEAD_FORM_DELIVERY_CONTEXT.officeLocal;
  const entries =
    hasRuntimePostalLocation || stepsHavePostalCodeInput(steps)
      ? {
          ...persistedFieldValues,
          location:
            postalLocationValues.location || postalLocationValues.postalCode,
          postalCode: postalLocationValues.postalCode,
        }
      : persistedFieldValues;

  return (
    <>
      {Object.entries(entries).map(([name, value]) => (
        <input key={name} name={name} type="hidden" value={value} />
      ))}
    </>
  );
}

export { PersistedFieldInputs };
